import { Injectable, inject } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { take } from "rxjs/operators";
import { AuthService } from "./auth.service";
import { ProfileService } from "./profile.service";
import { FunctionsService } from "./functions.service";
import { environment } from "../../environments/environment";

export interface PricingCatalogTier {
  currency: string;
  monthly: number;
  annual: number;
}

export interface PricingCatalogInfo {
  countryCode: string;
  currency: string;
  /**
   * VAT rate in percent (e.g. 20 for France). 0 for non-EU countries that BV
   * does not charge tax to. Drives the TTC ("tax-included") display in the
   * pricing widget per French Consumer Code Article L112-1.
   */
  vatRate: number;
  tiers: { [productCode: string]: PricingCatalogTier };
}

// Version aligned with the gatsby cache key (bv_country_info_v4) so both apps
// run on the same shape revision. Bumping invalidates earlier cache entries.
// v2: vatRate added to the response shape.
// v4: aligned with gatsby version (no shape change, key bump only).
// v5: invalidate after currency-restructure rollout — legacy users had
// profile.currency stuck at USD/EUR; the catalog response itself didn't
// change, but a stale cache could keep isCurrencyChange detection wrong.
// v6: invalidate caches written with empty country during the auth race
// (fetchAnonymous fired with country="", got FR via geo header, cached
// without a country tag — readCache's mismatch check then skipped).
// v7: invalidate after AR currency-override rollout (ARS → USD) so newsletter
// recipients see the new $19.99 Pro price instead of stale ARS amounts.
// v8: clubsite tier added to the response (Blind Valet Sites).
// v9: invalidate v8 entries cached in the window between the v8 client
// rollout and the getMyPricing/pricingInfo deploy that actually added the
// clubsite tier — those carry 24h of clubsite-less data.
const CACHE_KEY = "bv_pricing_catalog_v9";
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24h

/**
 * Caches the per-country pricing catalog response so subscription/pricing UI
 * opens instantly after the first fetch. Pre-fetched on profile load so by
 * the time a user reaches the pricing page, the data is usually already in
 * localStorage.
 *
 * Two backends:
 *   - Logged-in users: getMyPricing callable (profile.locationData.countryCode
 *     is the source of truth; backfills from x-appengine-country on first hit
 *     to anti-VPN-shop the user's country).
 *   - Anonymous: pricingInfo HTTP endpoint (uses x-appengine-country header).
 *
 * Cache invalidation:
 *   - 24h TTL
 *   - Tied to the user's resolved country code; if the country changes (rare),
 *     the cache is rebuilt automatically on next get().
 */
@Injectable({ providedIn: "root" })
export class PricingCatalogService {
  private inflight: Promise<PricingCatalogInfo | null> | null = null;
  private fbFunc = inject(FunctionsService);

  constructor(private auth: AuthService, private profServ: ProfileService) {}

  /**
   * Returns cached catalog data immediately if fresh; otherwise issues a
   * fetch (deduplicated) and caches the response. Returns null only if the
   * fetch itself fails — caller should render its own fallback.
   */
  async get(): Promise<PricingCatalogInfo | null> {
    const country = this.resolveCountry();
    const cached = this.readCache(country);
    if (cached) return cached;
    if (!this.inflight) {
      this.inflight = this.fetchWithAuth(country).finally(() => {
        this.inflight = null;
      });
    }
    return this.inflight;
  }

  // Wait for the first emission of auth state before deciding whether to
  // call the authenticated callable (profile.locationData wins) vs the
  // anonymous HTTP endpoint (geo header wins). Without this, a logged-in
  // user navigating directly to /subscription races the auth restore and
  // falls through to fetchAnonymous, which returns pricing for the user's
  // current geo IP instead of their profile country.
  private async fetchWithAuth(country: string): Promise<PricingCatalogInfo | null> {
    let user: any = null;
    try {
      user = await firstValueFrom(this.auth.user.pipe(take(1)));
    } catch {
      user = null;
    }
    if (user && user.uid) return this.fetchAuthenticated();
    return this.fetchAnonymous(country);
  }

  /** Synchronous accessor — returns cached data or null. Used to render
   *  immediately on component init without awaiting if data is already there. */
  getCached(): PricingCatalogInfo | null {
    return this.readCache(this.resolveCountry());
  }

  /** Fire-and-forget warm-up. Call from app startup / profile init so the
   *  pricing page is instant when the user reaches it. */
  prefetch(): void {
    this.get().catch(() => {});
  }

  /** Force-clear the cache. Useful after pricing config changes propagate. */
  clear(): void {
    try { localStorage.removeItem(CACHE_KEY); } catch {}
  }

  // --- internals ---

  private resolveCountry(): string {
    const c = this.profServ.latestProfile?.locationData?.countryCode;
    return (c || "").toString().toUpperCase().slice(0, 2);
  }

  private readCache(currentCountry: string): PricingCatalogInfo | null {
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as { data: PricingCatalogInfo; timestamp: number; country: string };
      if (!parsed || !parsed.data || !parsed.timestamp) return null;
      if (Date.now() - parsed.timestamp > CACHE_TTL_MS) return null;
      // Country mismatch invalidates the cache. If the caller has a
      // currentCountry (i.e. profile is loaded), require parsed.country to
      // match — including rejecting empty parsed.country (caches written
      // before the country was resolved). Without this, a stale "" cache
      // kept FR data alive for a Norwegian user.
      if (currentCountry && parsed.country !== currentCountry) return null;
      return parsed.data;
    } catch {
      return null;
    }
  }

  private writeCache(data: PricingCatalogInfo, country: string): void {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({ data, timestamp: Date.now(), country }));
    } catch {}
  }

  private async fetchAuthenticated(): Promise<PricingCatalogInfo | null> {
    try {
      const callable = this.fbFunc.httpsCallable("getMyPricing");
      const result = await callable({});
      const data = result && result.data;
      if (data && data.tiers) {
        this.writeCache(data, (data.countryCode || "").toString().toUpperCase());
        return data;
      }
      return null;
    } catch (e) {
      console.warn("[pricing-catalog] callable fetch failed", e);
      return null;
    }
  }

  private async fetchAnonymous(country: string): Promise<PricingCatalogInfo | null> {
    try {
      const projectId = (environment as any).firebase?.projectId || "blindvalet-production";
      const url = `https://us-central1-${projectId}.cloudfunctions.net/pricingInfo` +
        (country ? `?country=${encodeURIComponent(country)}` : "");
      const response = await fetch(url);
      if (!response.ok) return null;
      const data = (await response.json()) as PricingCatalogInfo;
      // Cache the country the SERVER resolved (data.countryCode), not the
      // input param — the param can be empty when called too early in the
      // bootstrap, and an empty cache key defeats the mismatch check.
      if (data && data.tiers) {
        this.writeCache(data, (data.countryCode || "").toString().toUpperCase());
      }
      return data;
    } catch (e) {
      console.warn("[pricing-catalog] fetch failed", e);
      return null;
    }
  }
}
